import {useState, useEffect, useMemo, useContext} from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Backdrop,
  Typography,
  Divider,
  FormControl,
  TextField,
  Alert,
  Button,
  DialogActions,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import {Link, useNavigate, useOutletContext, useParams, useSearchParams} from 'react-router-dom';
import {AutoFillAwareTextField, AuthBase, AuthHeading, AuthLogo, UseGoogleButton} from '../base';
import {postRegister} from '../../../api/auth';
import {isEmailValid} from '../../../utils/miscellaneous';
import {TelInput} from '../../../components/forms/telephoneInput';
import {googleAuthEnabled} from '../constants';
import AuthContext from '../../../context/AuthContext';
import {PrivacyContent} from '../../TermsAndPrivacy/privacyContent';
import mainLogo from '../../../images/logo_black_letters_2.png';
import '../../../App.css';
/**
 * Component for user registration.
 *
 * @returns {JSX.Element} - The rendered RegisterPage component.
 */

const commonStyleForTextField = {
  // General Input styles (targeting the root of the input field)
  '& .MuiOutlinedInput-root': {
    background: 'white !important', // Transparent background for outer input container
    borderRadius: '40px !important', // Rounded corners for the outer input container
    boxShadow: 'none !important', // Remove any box-shadow on focus
  },

  // Styles when the input is focused
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: 'none', // Ensure no shadow on focus
    background: 'white', // White background on focus
  },

  // Styles for the input itself (inner input element)
  '& .MuiOutlinedInput-input': {
    backgroundColor: 'white !important', // White background for input field
    borderRadius: '40px !important', // Rounded corners for the input
    color: 'var(--aimdFlowSecondaryDarkTextColor)', // Set the text color
    fontSize: '16px', // Font size
    fontWeight: 500, // Font weight
  },

  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
    background: 'linear-gradient(to right, #EAF9F9, #D0F4F4) !important', // Background color for the input when focused
  },
  // Notched outline (the border for the input field)
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--aimdFlowPrimaryColor) !important', // Default border color
    borderRadius: '40px !important', // Rounded corners for the outline
  },

  // Ensure no box-shadow when the input is hovered
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--aimdFlowPrimaryColor)', // Border color on hover
    boxShadow: '0px 8px 18px 0px rgba(0, 0, 0, 0.10)', // Light shadow on hover
  },

  // Focused state for the label color
  '& .MuiFormLabel-root': {
    color: 'var(--aimdFlowSecondaryDarkTextColor)', // Label color when not focused
  },

  // Focused label color change
  '& .Mui-focused .MuiFormLabel-root': {
    color: 'var(--aimdFlowPrimaryColor)', // Change label color on focus
  },

  // Ensuring label position and styling when input is focused
  '& .MuiFormLabel-root.Mui-focused': {
    color: 'var(--aimdFlowPrimaryColor)', // Color of the label when the input is focused
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: 'var(--aimdFlowSecondaryDarkTextColor)', // Color for the placeholder
    opacity: 1, // Ensures full opacity for the placeholder text
  },
};

const commonStyleForInputLabelProp = {
  color: 'var(--aimdFlowSecondaryDarkTextColor)',
};
export const RegisterPage = () => {
  const {setEmail, setPassword: setSavedPassword} = useOutletContext();
  const theme = useTheme();

  const [params, _] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(params.get('token') || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setFormEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registrationCode, setRegistrationCode] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailSubscription, setEmailSubscription] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationConflict, setValidationConflict] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [canRedirect, setCanRedirect] = useState(false);

  const {loginUser, setUser, unauthorized, setUnauthorized, authTokenSetter} = useContext(AuthContext);
  const [openModalPrivacy, setOpenModalPrivacy] = useState(false);

  const formIsFilled = useMemo(() => {
    return email !== '' && password.length >= 8 && confirmPassword.length >= 8 && firstName !== '';
  }, [email, password.length, confirmPassword.length, firstName]);

  const navigateNext = () => {
    if (token) {
      navigate('/results');
    } else {
      navigate('/plans', {
        state: {
          flowComingFromRegitrstionPage: true, // Pass the state here
        },
      });
    }
  };

  useEffect(() => {
    // Clear password, confirm password, and email textfields
    setPassword('');
    setConfirmPassword('');
    setEmail('');
  }, []);

  useEffect(() => {
    if (!formIsFilled || !canRedirect || !isEmailValid(email) || isPhoneNumberValid) {
      return;
    }
  }, [email, canRedirect]);

  // Function to handle visibility of password (eye toggle)
  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };
  const handlePrivacyPolicy = () => {
    setOpenModalPrivacy(true);
  };

  // Function to handle visibility of confirm password (eye toggle)
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(show => !show);

  const handleMouseDownConfirmPassword = event => {
    event.preventDefault();
  };

  const handleRegister = async () => {
    setLoading(true);

    if (firstName === undefined || firstName === '' || !/\S/.test(firstName)) {
      setErrorMessage('Please enter a valid first name');

      setValidationConflict(true);
      setLoading(false);
      return;
    }

    if (lastName !== undefined && lastName !== '' && !/\S/.test(lastName)) {
      setErrorMessage('Last name cannot be only whitespace');
      setValidationConflict(true);
      setLoading(false);
      return;
    }

    if (!isPhoneNumberValid && phoneNumber !== '') {
      setErrorMessage('Phone number is not valid.');
      setValidationConflict(true);
      setLoading(false);
      return;
    }

    if (!isEmailValid(email)) {
      setErrorMessage('Email is not valid.');
      setValidationConflict(true);
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setValidationConflict(true);
      setLoading(false);
      return;
    }
    setErrorMessage('');
    setValidationConflict(false);

    try {
      const res = await postRegister(
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        registrationCode,
        emailSubscription,
        token === '' ? null : token,
      );

      setValidationConflict(false);
      setSavedPassword(password);
      setEmail(email); //redirection ocurring from useEffect
      setCanRedirect(true);
      authTokenSetter(res);
      navigateNext();
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            setLoading(false);
            setValidationConflict(true);
            setErrorMessage(
              'The Registration Code used is not valid. Please leave it blank or enter a valid registration code.',
            );
            break;
          default:
            setLoading(false);
            setValidationConflict(true);
            setErrorMessage(error?.response?.data?.detail ?? 'An error ocurred, please try again later');
            break;
        }
      }
    }
  };

  const handleEmailSubscription = event => {
    setEmailSubscription(event.target.checked);
  };

  return (
    <AuthBase>
      <Stack spacing={{xs: 1, md: 1.5}} alignItems="start" justifyContent="stretch" sx={{width: 1}}>
        <Stack
          sx={{textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}
        >
          <img src={mainLogo} width="150px" height="auto" />
          <AuthHeading>Create your account</AuthHeading>
        </Stack>
        {googleAuthEnabled && (
          <>
            <Divider sx={{width: 1, color: 'gray'}} />
            <UseGoogleButton
              navigate={navigateNext}
              factor="register"
              setUser={setUser}
              setUnauthorized={setUnauthorized}
              token={token}
            />
            <Divider sx={{width: 1, color: 'gray'}}>
              <Typography sx={{color: 'var(--aimdFlowSecondaryDarkTextColor)'}}>Or register with your email</Typography>
            </Divider>
          </>
        )}
        {validationConflict ? (
          <Alert
            severity="error"
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              marginY: '10px !important',
            }}
          >
            {errorMessage}
          </Alert>
        ) : (
          ''
        )}
        <Stack direction="row" spacing={2} fullWidth sx={{width: 1}} justifyContent="space-between">
          <FormControl fullWidth>
            <TextField
              value={firstName}
              onChange={e => {
                setFirstName(e.target.value);
              }}
              sx={commonStyleForTextField}
              InputLabelProps={{
                style: commonStyleForInputLabelProp, // Use the variable for label styles
              }}
              placeholder="First Name*"
              autoComplete="off"
              variant="outlined"
              fullWidth
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              value={lastName}
              onChange={e => {
                setLastName(e.target.value);
              }}
              autoComplete="off"
              sx={commonStyleForTextField}
              InputLabelProps={{
                style: commonStyleForInputLabelProp, // Use the variable for label styles
              }}
              placeholder="Last Name"
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Stack>
        <FormControl fullWidth>
          <AutoFillAwareTextField
            customSx={true}
            isInputBgColorOnFocus={true}
            autoComplete="new-email"
            value={email}
            onChange={e => {
              setFormEmail(e.target.value);
            }}
            placeholder="Email Address*"
            disabled={token ? true : false}
            variant="outlined"
            fullWidth
          />
        </FormControl>
        <FormControl fullWidth>
          <TelInput
            value={phoneNumber}
            onChange={(value, isValid) => {
              setPhoneNumber(value);
              setIsPhoneNumberValid(isValid);
            }}
            customCss={{
              border: '1px solid var(--aimdFlowPrimaryColor)',
              borderRadius: '40px',
              borderColor: 'var(--aimdFlowPrimaryColor)',
              color: 'var(--aimdFlowSecondaryDarkTextColor)',
              background: 'linear-gradient(to right, #EAF9F9, #D0F4F4)',
              backgroundColor: 'linear-gradient(to right, #EAF9F9, #D0F4F4)',
            }}
          />
        </FormControl>
        <FormControl fullWidth>
          <AutoFillAwareTextField
            autoComplete="new-password"
            isInputBgColorOnFocus={true}
            customSx={true}
            helperText={'Minimum length: 8 characters'}
            value={password}
            onChange={e => {
              setPassword(e.target.value);
            }}
            placeholder="Password*"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    ) : (
                      <Visibility sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '40px',
                color: 'var(--aimdFlowSecondaryDarkTextColor)',
                fontWeight: 500,
                fontSize: '16px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                },
              },
            }}
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
          />
        </FormControl>
        <FormControl fullWidth>
          <AutoFillAwareTextField
            isInputBgColorOnFocus={true}
            autoComplete="new-password"
            customSx={true}
            helperText={'Minimum length: 8 characters'}
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(e.target.value);
            }}
            placeholder="Confirm password*"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOff sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    ) : (
                      <Visibility sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '40px',
                color: 'var(--aimdFlowSecondaryDarkTextColor)',
                fontWeight: 500,
                fontSize: '16px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                },
              },
            }}
            variant="outlined"
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
          />
        </FormControl>
        <FormControlLabel
          labelPlacement="end"
          control={
            <Checkbox
              checked={emailSubscription}
              onChange={handleEmailSubscription}
              sx={{
                color: 'var(--aimdFlowPrimaryColor)', // Color for the unchecked state
                '&.Mui-checked': {
                  color: 'var(--aimdFlowPrimaryColor)', // Color for the checked state
                },
                marginTop: '-8px',
              }}
            />
          }
          label={
            <Typography>
              I consent to receiving emails from AI-MD. I understand that I can unsubscribe at any time, and my
              information will be handled in accordance with{' '}
              <Link onClick={handlePrivacyPolicy}>AI-MD's Privacy Policy.</Link>
            </Typography>
          }
          sx={{display: 'flex', alignItems: 'flex-start'}}
        />
        <LoadingButton
          disabled={!formIsFilled}
          onClick={handleRegister}
          loading={loading}
          fullWidth
          variant="outlined"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: '40px', // Corrected typo here (bordeRadius -> borderRadius)
            height: '65px',
            fontWeight: 600,
            fontSize: '20px',
            background: 'var(--aimdLinearGradientBtnColor)',
            color: '#fff',
            '&:hover': {
              background: 'rgb(141, 209, 210)', // Adjust hover background color if needed
              borderColor: 'rgb(141, 209, 210)', // Add border color for hover if necessary
            },
            '&.Mui-disabled': {
              background: 'rgb(161,229,230)', // Custom background color
              color: 'rgba(255, 255, 255, 0.70)', // Text color
            },
          }}
        >
          Continue
        </LoadingButton>
        <Typography sx={{width: 1, textAlign: 'center', fontWeight: 500}}>
          Already have an account?
          <Typography>
            <Link
              to="/user-login"
              style={{
                textDecorationColor: 'var(--aimdFlowSecondaryDarkTextColor)',
                color: 'var(--aimdFlowSecondaryDarkTextColor)',
              }}
            >
              Login
            </Link>
          </Typography>
        </Typography>
      </Stack>
      {/* Modal box when user clicks privacy policy link */}
      <Dialog
        open={openModalPrivacy}
        maxWidth="sm"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          position: 'absolute',
          top: theme.spacing(5),
          paddingY: theme.spacing(2),
          '& .MuiDialog-paperScrollPaper': {
            borderRadius: 5,
          },
        }}
      >
        <DialogTitle sx={{paddingY: theme.spacing(3)}}>
          <Typography component="span" variant="h6">
            PRIVACY POLICY
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingY: theme.spacing(5),
            paddingRight: theme.spacing(3),
          }}
        >
          <PrivacyContent isPage={false} />
        </DialogContent>
        <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
          <Button variant="text" onClick={() => setOpenModalPrivacy(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AuthBase>
  );
};
